import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  CreditCard, 
  Receipt, 
  Settings, 
  ExternalLink, 
  Check, 
  Clock,
  AlertTriangle,
  Zap,
  TrendingUp,
  Download,
  RefreshCw,
  Star,
  Sparkles
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import PromoCodeInput from "@/components/PromoCodeInput";

const TIER_INFO = {
  free: { name: "Free", color: "slate", price: 0, icon: Zap },
  pro: { name: "Pro", color: "sky", price: 29, icon: Star },
  growth: { name: "Growth", color: "cyan", price: 79, icon: Sparkles }
};

export default function BillingDashboard({ user, usage, onUpgrade }) {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, invRes, tiersRes] = await Promise.all([
        api.get("/billing/subscription").catch(() => ({ data: null })),
        api.get("/billing/invoices").catch(() => ({ data: { invoices: [] } })),
        api.get("/billing/tiers").catch(() => ({ data: { tiers: [] } }))
      ]);
      setSubscription(subRes.data);
      setInvoices(invRes.data?.invoices || []);
      setTiers(tiersRes.data?.tiers || []);
    } catch (e) {
      console.error("Error fetching billing data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", {
        return_url: window.location.href
      });
      window.location.href = res.data.url;
    } catch (e) {
      console.error("Error opening portal:", e);
      toast.error(e.response?.data?.detail || "Failed to open billing portal");
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async (tierId) => {
    if (tierId === "free" || tierId === user?.tier) return;

    setUpgrading(tierId);
    try {
      const response = await api.post("/billing/checkout", {
        tier: tierId,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (e) {
      console.error("Error creating checkout:", e);
      if (e.response?.status === 403) {
        toast.error("Demo accounts can't subscribe — sign in with your real account to upgrade.");
      } else {
        toast.error("Failed to start checkout. Please try again.");
      }
      setUpgrading(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const tier = subscription?.tier || user?.tier || "free";
  const tierInfo = TIER_INFO[tier];
  const usageData = subscription?.usage || usage;
  const limitsData = subscription?.limits || { gen_cap_month: usage?.gen_cap_month || 10 };
  const usagePercent = limitsData?.gen_cap_month 
    ? Math.round((usageData?.gen_count_month || 0) / limitsData.gen_cap_month * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500">Billing & Plans</h1>
          <p className="text-indigo-400 text-sm mt-1">Manage your subscription and payment</p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "overview" 
              ? "bg-indigo-500/20 text-indigo-400" 
              : "text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50"
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("plans")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "plans" 
              ? "bg-indigo-500/20 text-indigo-400" 
              : "text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50"
          }`}
        >
          Plans & Pricing
        </button>
        <button
          onClick={() => setActiveTab("invoices")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "invoices" 
              ? "bg-indigo-500/20 text-indigo-400" 
              : "text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50"
          }`}
        >
          Invoices
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Current Plan Card */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <Zap className="w-5 h-5 text-indigo-400" />
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl bg-${tierInfo.color}-500/20 flex items-center justify-center`}>
                    <tierInfo.icon className={`w-8 h-8 text-${tierInfo.color}-400`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-indigo-500">{tierInfo.name}</h2>
                      {subscription?.is_trial && (
                        <Badge className="bg-indigo-500/20 text-indigo-400">Trial</Badge>
                      )}
                      {subscription?.subscription_status === "active" && (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      )}
                    </div>
                    <p className="text-indigo-400">
                      {tierInfo.price === 0 ? "Free forever" : `$${tierInfo.price}/month`}
                    </p>
                    {subscription?.is_trial && subscription?.trial_ends_at && (
                      <p className="text-sm text-indigo-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Trial ends {formatDate(subscription.trial_ends_at)}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  {tier === "free" && (
                    <button
                      onClick={() => setActiveTab("plans")}
                      className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <TrendingUp className="w-4 h-4" />
                      Upgrade
                    </button>
                  )}
                  <button
                    onClick={openCustomerPortal}
                    disabled={portalLoading}
                    className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="manage-subscription-btn"
                  >
                    {portalLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Settings className="w-4 h-4" />
                    )}
                    Manage Subscription
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usage Card */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Usage This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-indigo-400">AI Generations</span>
                  <span className="text-indigo-500 font-medium">
                    {usageData?.gen_count_month || 0} / {limitsData?.gen_cap_month || 0}
                  </span>
                </div>
                <Progress value={usagePercent} className="h-2" />
                <p className="text-xs text-slate-400">
                  {(limitsData?.gen_cap_month || 0) - (usageData?.gen_count_month || 0)} generations remaining
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method Card */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-indigo-500 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-indigo-400" />
                  Payment Method
                </CardTitle>
                <button
                  onClick={openCustomerPortal}
                  className="text-sm text-indigo-400 hover:text-indigo-400 flex items-center gap-1"
                >
                  Update <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {subscription?.stripe?.has_payment_method ? (
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                  <div className="w-10 h-10 bg-slate-400 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-indigo-500 font-medium">Card on file</p>
                    <p className="text-sm text-slate-400">Managed via Stripe</p>
                  </div>
                  <Check className="w-5 h-5 text-green-400 ml-auto" />
                </div>
              ) : (
                <div className="text-center py-6">
                  <CreditCard className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-indigo-400">No payment method on file</p>
                  <button
                    onClick={openCustomerPortal}
                    className="mt-3 text-sm text-indigo-400 hover:text-indigo-400"
                  >
                    Add payment method →
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === "plans" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tierData) => {
            const Icon = TIER_INFO[tierData.id]?.icon || Zap;
            const isCurrentTier = tier === tierData.id;
            const isUpgrade = tierData.id !== 'free' && tierData.id !== tier;
            
            return (
              <Card 
                key={tierData.id} 
                className={`bg-slate-800/50 border-slate-700/50 relative overflow-hidden ${
                  tierData.id === 'pro' ? 'border-indigo-500/50 ring-1 ring-indigo-500/30' : ''
                } ${isCurrentTier ? 'border-green-500/50' : ''}`}
              >
                {tierData.id === 'pro' && (
                  <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Popular
                  </div>
                )}
                {isCurrentTier && (
                  <div className="absolute top-0 left-0 bg-green-500 text-indigo-500 text-xs px-3 py-1 rounded-br-lg font-medium">
                    Current
                  </div>
                )}
                
                <CardContent className="p-6 pt-8">
                  <div className="text-center mb-6">
                    <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                      tierData.id === 'free' ? 'bg-slate-400' :
                      tierData.id === 'pro' ? 'bg-indigo-500/20' : 'bg-indigo-500/20'
                    }`}>
                      <Icon className={`w-7 h-7 ${
                        tierData.id === 'free' ? 'text-indigo-400' :
                        tierData.id === 'pro' ? 'text-indigo-400' : 'text-indigo-400'
                      }`} />
                    </div>
                    <h3 className="text-xl font-bold text-indigo-500">{tierData.name}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-indigo-500">${tierData.price}</span>
                      {tierData.price > 0 && <span className="text-indigo-400">/month</span>}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {tierData.features?.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-400">
                        <Check className={`w-4 h-4 ${
                          tierData.id === 'free' ? 'text-slate-400' :
                          tierData.id === 'pro' ? 'text-indigo-400' : 'text-indigo-400'
                        }`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleUpgrade(tierData.id)}
                    disabled={isCurrentTier || tierData.id === 'free' || upgrading}
                    className={`w-full py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                      isCurrentTier 
                        ? 'bg-green-500/20 text-green-400 cursor-default'
                        : tierData.id === 'free'
                        ? 'bg-slate-400 text-indigo-400 cursor-default'
                        : tierData.id === 'pro'
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                        : 'bg-indigo-500 hover:bg-slate-600 text-white'
                    }`}
                    data-testid={`upgrade-${tierData.id}-btn`}
                  >
                    {upgrading === tierData.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : isCurrentTier ? (
                      <>Current Plan<Check className="w-4 h-4" /></>
                    ) : tierData.id === 'free' ? (
                      'Free Forever'
                    ) : (
                      <>Upgrade<ExternalLink className="w-4 h-4" /></>
                    )}
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === "invoices" && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-indigo-400" />
              Invoice History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length > 0 ? (
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        invoice.status === "paid" ? "bg-green-400" :
                        invoice.status === "open" ? "bg-stone-400" : "bg-slate-400"
                      }`} />
                      <div>
                        <p className="text-indigo-500 font-medium">
                          {invoice.number || `Invoice ${invoice.id.slice(-8)}`}
                        </p>
                        <p className="text-sm text-slate-400">
                          {formatDate(invoice.created)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-indigo-500 font-medium">
                        {formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}
                      </span>
                      <Badge className={
                        invoice.status === "paid" ? "bg-green-500/20 text-green-400" :
                        invoice.status === "open" ? "bg-indigo-500/20 text-indigo-400" :
                        "bg-slate-500/20 text-indigo-400"
                      }>
                        {invoice.status}
                      </Badge>
                      {invoice.invoice_pdf && (
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-slate-400/50 rounded transition-colors"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Receipt className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-indigo-400">No invoices yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Invoices will appear here after your first payment
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Promo Code Section */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500">Promo Code</CardTitle>
        </CardHeader>
        <CardContent>
          <PromoCodeInput 
            onSuccess={(newTier) => {
              toast.success(`Upgraded to ${newTier}!`);
              onUpgrade?.();
              fetchData();
            }}
          />
        </CardContent>
      </Card>

      {/* FAQ */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500">Billing FAQ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-slate-600">When does my usage reset?</h4>
            <p className="text-sm text-indigo-400">Usage resets on the 1st of each month.</p>
          </div>
          <div>
            <h4 className="font-medium text-slate-600">Can I downgrade my plan?</h4>
            <p className="text-sm text-indigo-400">Yes, you can downgrade anytime via the Stripe customer portal.</p>
          </div>
          <div>
            <h4 className="font-medium text-slate-600">How do payments work?</h4>
            <p className="text-sm text-indigo-400">Payments are processed securely through Stripe. You can manage payment methods in the customer portal.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
